import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, UtilityOperator, UtilityOperatorType } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['ENERGY_OFFICER'];
const OPERATOR_TYPES: UtilityOperatorType[] = [
  'ELECTRICITY_UTILITY',
  'WATER_UTILITY',
  'INDEPENDENT_POWER_PRODUCER',
  'FUEL_RETAILER',
  'FUEL_IMPORTER',
  'FUEL_STORAGE_OPERATOR',
];

/** Regulator-curated utility operator registry (PUC, IPPs, fuel companies) — not self-service (M35). */
export function Operators() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENERGY_OFFICER') ?? false;
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [type, setType] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (type) params.set('type', type);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m35/operators?${params.toString()}`;
  }, [type, appliedQ]);
  const listQ = useApiQuery<Page<UtilityOperator>>(path);

  return (
    <div>
      <PageHeader
        title="Utility Operators"
        subtitle="Regulator-curated registry of electricity/water utilities, IPPs, and fuel retailers/importers/storage operators (M35)."
        actions={
          isOfficer ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Register operator
            </button>
          ) : undefined
        }
      />

      <form
        className="mb-4 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAppliedQ(q);
        }}
      >
        <input className="field-input !w-56" placeholder="Name or registration no…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-auto" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          {OPERATOR_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary">
          Search
        </button>
      </form>

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No operators registered" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Name</th>
                <th className="table-head">Type</th>
                <th className="table-head">Registration no</th>
                <th className="table-head">Active</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(o.id)}>
                  <td className="table-cell font-medium">{o.name}</td>
                  <td className="table-cell">
                    <StatusBadge status={o.type} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{o.registrationNo ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={o.active ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <OperatorDetailModal
          id={selectedId}
          isOfficer={isOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function OperatorDetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<UtilityOperator>(`/api/v1/m35/operators/${id}`);
  const o = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const save = async (patch: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m35/operators/${id}`, patch);
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={o ? o.name : 'Utility operator'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {o ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Type" value={<StatusBadge status={o.type} />} />
            <Detail label="Active" value={<StatusBadge status={o.active ? 'ACTIVE' : 'CLOSED'} />} />
            <Detail label="Registration no" value={o.registrationNo ?? '—'} />
            <Detail label="Contact" value={o.contactEmail ?? o.contactPhone ?? '—'} />
          </div>
          {isOfficer ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Update contact / status</div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Contact email">
                  <input className="field-input" placeholder={o.contactEmail ?? ''} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                </Field>
                <Field label="Contact phone">
                  <input className="field-input" placeholder={o.contactPhone ?? ''} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                </Field>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn-secondary"
                  disabled={busy || (!contactEmail && !contactPhone)}
                  onClick={() => save({ ...(contactEmail ? { contactEmail } : {}), ...(contactPhone ? { contactPhone } : {}) })}
                >
                  Save contact
                </button>
                <button className={o.active ? 'btn-danger' : 'btn-success'} disabled={busy} onClick={() => save({ active: !o.active })}>
                  {o.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </div>
          ) : null}
          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Registered {formatDateTime(o.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState<UtilityOperatorType>('ELECTRICITY_UTILITY');
  const [registrationNo, setRegistrationNo] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m35/operators', {
        name,
        type,
        registrationNo: registrationNo || undefined,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register operator');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register utility operator" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as UtilityOperatorType)}>
              {OPERATOR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Registration no (optional)">
            <input className="field-input" value={registrationNo} onChange={(e) => setRegistrationNo(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact email (optional)">
            <input className="field-input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </Field>
          <Field label="Contact phone (optional)">
            <input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || name.trim().length < 2} onClick={submit}>
            {busy ? 'Registering…' : 'Register'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
